import { type FC, type MouseEventHandler, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type OfferV11,
  type PimProductV11,
  PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'
import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Button } from 'base-ui'

import {
  cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier,
  useMultipleProductsAddToCart,
} from '../../../../cart-intermediate'
import { Experiment } from '../../../../experiments/components/Experiment'
import { useExperimentsContext } from '../../../../experiments/components/ExperimentsContext'
import { ExperimentVariant } from '../../../../experiments/components/ExperimentVariant'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../../experiments/hooks'
import { EXPERIMENT_NAME_CART_INTERMEDIATE_DRAWER } from '../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../../experiments/model/EXPERIMENT_VARIATION'
import { type ExperimentType } from '../../../../experiments/model/Experiment.types'
import { experimentAccessorIsSaeOnly } from '../../../../experiments/model/experimentAccessor'
import {
  useOpenableControls,
  useRerenderAndExecute,
} from '../../../../generic-hooks'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../global-config'
import {
  queryStringEncode,
  urlResolverGetCartIntermediatePage,
} from '../../../../url-handling'
import { productAccessorGetVariantCode } from '../../../model/productAccessor/productAccessorGetVariantCode'
import {
  ProductPageRxByPrincipleDialog,
  type RxByPrincipleControls,
} from '../../../rx-by-principle'
import { useProductPageContext } from '../../productPageContext'

const getBuyTogetherAddToCartUrl = ({
  currentOffer,
  currentVariant,
  experiments,
  globalConfig,
  recommendedProduct,
  rxByPrinciple,
}: {
  currentOffer: OfferV11
  currentVariant: PimProductV11
  experiments: ExperimentType[]
  globalConfig: PublicGlobalConfig
  recommendedProduct: CrossSellProduct
  rxByPrinciple: RxByPrincipleControls
}): string => {
  let queryParams: Record<string, unknown> = {
    cartAction: 'add',
    eventName: 'addToCart_bought_together',
    eventType: 'conversion',
  }

  if (experimentAccessorIsSaeOnly(experiments)) {
    queryParams = {
      ...queryParams,
      crossSellOfferId: recommendedProduct.offerId,
      crossSellPid: recommendedProduct.id,
      offer: [currentOffer.offerId, recommendedProduct.offerId],
      originalOfferId: currentOffer.offerId,
      originalPid: currentVariant.upid,
      prescriptionType: PrescriptionPrescriptionTypeEnum.NoPrescription,
      product: [currentVariant.upid, recommendedProduct.id],
      quantity: 1,
    }
  } else {
    queryParams['product[]'] = [currentVariant.upid, recommendedProduct.id]
  }

  if (rxByPrinciple.isReasonRequired && rxByPrinciple.selectedReason) {
    queryParams[rxByPrinciple.queryParamName] = rxByPrinciple.selectedReason
  }

  return `${urlResolverGetCartIntermediatePage(
    globalConfig,
  )}${queryStringEncode(queryParams)}`
}

type ProductCrossSellBuyTogetherAddToCartProps = {
  currentOffer: OfferV11
  currentVariant: PimProductV11
  onGtmDataLayerPush: () => void
  recommendedProduct: CrossSellProduct
}

export const ProductCrossSellBuyTogetherAddToCart: FC<
  ProductCrossSellBuyTogetherAddToCartProps
> = ({
  currentOffer,
  currentVariant,
  onGtmDataLayerPush,
  recommendedProduct,
}) => {
  const { experiments } = useExperimentsContext()
  const globalConfig = useGlobalConfigContext()
  const rxByPrincipleDialogControls = useOpenableControls()
  const rerenderAndExecute = useRerenderAndExecute()

  const { currentPrescriptionType, product, rxByPrincipleControls } =
    useProductPageContext()

  const submitButtonRef = useRef<HTMLAnchorElement>(null)
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const currentOfferRedpoints =
    cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
      currentOffer,
    )

  const onClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (
      rxByPrincipleControls.isReasonRequired &&
      !rxByPrincipleControls.selectedReason
    ) {
      event.preventDefault()
      rxByPrincipleDialogControls.handleOpen()

      return
    }

    onGtmDataLayerPush()
    trackClick()
  }

  const products = [
    {
      id: currentOffer.offerId,
      product,
      redpoints: currentOfferRedpoints,
    },
    {
      id: recommendedProduct.offerId,
      product: recommendedProduct,
      redpoints: Number(recommendedProduct.redpoints),
    },
  ]

  const { handleAddToCart } = useMultipleProductsAddToCart({
    currentPrescriptionType,
    products,
  })

  return (
    <>
      <Experiment names={[EXPERIMENT_NAME_CART_INTERMEDIATE_DRAWER]}>
        <ExperimentVariant name={EXPERIMENT_VARIATION.DEFAULT}>
          <Button<'a'>
            className="mt-7 w-full"
            data-qa-id="buy-together-with-button"
            href={getBuyTogetherAddToCartUrl({
              currentOffer,
              currentVariant,
              experiments,
              globalConfig,
              recommendedProduct,
              rxByPrinciple: rxByPrincipleControls,
            })}
            // @ts-expect-error name prop is needed only for GTM tracking, would be good to change it (for data-* attribute), contact Damian Dudek
            name={`buy-together-${currentVariant.upid}-${recommendedProduct.id}`}
            onClick={onClick}
            ref={submitButtonRef}
            slots={{ root: 'a' }}
          >
            <FormattedMessage id="buyTogetherWith.button.label.addBothToCart" />
          </Button>
        </ExperimentVariant>
        <ExperimentVariant name={EXPERIMENT_VARIATION.V1}>
          <Button<'a'>
            className="mt-7 w-full"
            data-qa-id="buy-together-with-button"
            // @ts-expect-error name prop is needed only for GTM tracking, would be good to change it (for data-* attribute), contact Damian Dudek
            name={`buy-together-${currentVariant.upid}-${recommendedProduct.id}`}
            onClick={(event): void => {
              void handleAddToCart()
              onClick(event)
            }}
            ref={submitButtonRef}
            slots={{ root: 'a' }}
          >
            <FormattedMessage id="buyTogetherWith.button.label.addBothToCart" />
          </Button>
        </ExperimentVariant>
      </Experiment>
      {rxByPrincipleControls.isReasonRequired ? (
        <ProductPageRxByPrincipleDialog
          autoOpenOnLocationHash={false}
          // key is needed to reset form state when user clientside navigates to other product
          key={productAccessorGetVariantCode(product)}
          onClose={rxByPrincipleDialogControls.handleClose}
          onOpen={rxByPrincipleDialogControls.handleOpen}
          onReasonSelect={(reason) => {
            rxByPrincipleDialogControls.handleClose()
            rxByPrincipleControls.setSelectedReason(reason)
            rerenderAndExecute(() => {
              submitButtonRef.current?.click()
            })
          }}
          open={rxByPrincipleDialogControls.isOpen}
          productName={product.name}
          reasonSet={rxByPrincipleControls.reasonSet}
        />
      ) : null}
    </>
  )
}
